<template>
  <VcLayout column class="leads-widget pa-6" data-qa="LeadsWidget">
    <VcLayout justify-space-between align-center class="leads-widget__header">
      <div class="leads-widget__title" data-qa="LeadsWidgetTitle">
        {{ $t('widget.leads_widget.title') }}
      </div>
    </VcLayout>

    <VcLayout class="leads-widget__wrapper mt-4">
      <VcLayout column class="leads-widget__data">
        <VcSkeleton v-if="isLoading" type="paragraph" width="100%" height="30px" data-qa="LeadsWidgetSkeleton"/>
        <template v-else>
          <div class="leads-widget__metric">
            <div class="leads-widget__metric-label leads-widget__metric-label--inline">
              <span>{{ $t('widget.leads_widget.new_leads') }}</span>
              <div class="leads-widget__period-selector" data-qa="LeadsWidgetPeriodSelector">
                <button
                  type="button"
                  class="leads-widget__period-trigger"
                  :aria-expanded="isPeriodMenuOpen.toString()"
                  data-qa="LeadsWidgetPeriodTrigger"
                  @click="togglePeriodMenu"
                >
                  {{ selectedPeriodLabel }}
                  <span class="leads-widget__period-caret"></span>
                </button>
                <div v-if="isPeriodMenuOpen" class="leads-widget__period-menu">
                  <button
                    v-for="period in periodOptions"
                    :key="period.value"
                    type="button"
                    class="leads-widget__period-option"
                    :class="{ 'leads-widget__period-option--selected': selectedPeriod === period.value }"
                    :data-qa="`LeadsWidgetPeriod-${period.value}`"
                    @click="selectPeriod(period.value)"
                  >
                    {{ period.label }}
                  </button>
                </div>
              </div>
            </div>
            <div
              class="leads-widget__metric-value leads-widget__metric-value--primary"
              :class="{ 'leads-widget__metric-value--clickable': isNewLeadsLinkable }"
              :role="isNewLeadsLinkable ? 'button' : null"
              :tabindex="isNewLeadsLinkable ? 0 : null"
              data-qa="LeadsWidgetNewLeads"
              @click="handleNewLeadsClick"
              @keydown.enter="handleNewLeadsClick"
            >
              {{ newLeads }}
            </div>
          </div>

          <div
            v-if="showAiReceptionistMetrics"
            class="leads-widget__metric mt-4"
          >
            <div
              v-if="isAiReceptionistInstalled"
              class="leads-widget__metric-action"
              role="button"
              tabindex="0"
              data-qa="LeadsWidgetAiReceptionistChats"
              @click="handleAiReceptionistMetricClick"
              @keydown.enter="handleAiReceptionistMetricClick"
            >
              <div class="leads-widget__metric-label">{{ $t('widget.leads_widget.ai_receptionist_chats') }}</div>
              <div class="leads-widget__metric-value leads-widget__metric-value--primary">
                {{ leadsWidgetData.ai_receptionist_chats }}
              </div>
            </div>
            <template v-else>
              <div class="leads-widget__metric-label">{{ $t('widget.leads_widget.ai_receptionist_chats') }}</div>
              <div class="leads-widget__setup-text" data-qa="LeadsWidgetAiReceptionistSetup">
                <span>{{ $t('widget.leads_widget.ai_receptionist_not_installed') }} -</span>
                <button
                  type="button"
                  class="leads-widget__setup-link"
                  data-qa="LeadsWidgetAiReceptionistSetupLink"
                  @click="navigateToAiReceptionist"
                >
                  {{ $t('widget.leads_widget.ai_receptionist_setup_now') }}
                </button>
              </div>
            </template>
          </div>

          <div v-if="sourceRows.length" class="leads-widget__sources mt-5" data-qa="LeadsWidgetSources">
            <div class="leads-widget__section-title mb-2">{{ $t('widget.leads_widget.top_sources') }}</div>
            <div
              v-for="source in sourceRows"
              :key="source.source_key"
              class="leads-widget__source-row"
              :class="{ 'leads-widget__source-row--clickable': source.linkable }"
              :role="source.linkable ? 'button' : null"
              :tabindex="source.linkable ? 0 : null"
              data-qa="LeadsWidgetSourceRow"
              @click="handleSourceClick(source)"
              @keydown.enter="handleSourceClick(source)"
            >
              <span class="leads-widget__source-label">
                <span class="leads-widget__source-dot" :style="{ backgroundColor: source.color }"></span>
                {{ source.label }}
              </span>
              <span class="leads-widget__source-separator">|</span>
              <span class="leads-widget__source-count">{{ source.count }}</span>
            </div>
          </div>
        </template>
      </VcLayout>

      <VcLayout column class="leads-widget__chart">
        <VcLayout justify-space-between align-start class="leads-widget__chart-header mb-3">
          <div class="leads-widget__section-title">{{ $t('widget.leads_widget.leads_over_time') }}</div>
          <div class="leads-widget__chart-periods" data-qa="LeadsWidgetChartPeriodSelector">
            <button
              v-for="period in chartPeriodOptions"
              :key="period.value"
              type="button"
              class="leads-widget__chart-period"
              :class="{ 'leads-widget__chart-period--selected': selectedChartPeriod === period.value }"
              :data-qa="`LeadsWidgetChartPeriod-${period.value}`"
              @click="handleChartPeriodChange(period.value)"
            >
              {{ period.label }}
            </button>
          </div>
        </VcLayout>
        <BarChart
          v-if="hasLoadedChart"
          :chart-data="chartData"
          :chart-options="chartOptions"
          :legend-display="false"
          :y-axis-grid-line-display="hasChartData"
          :x-axis-grid-line-display="false"
          :y-axis-tick-callback="formatYAxisTick"
          :tooltip-callback="formatTooltip"
          :tooltip-title-callback="formatTooltipTitle"
          :aria-label="$t('widget.leads_widget.chart_aria_label')"
          class="leads-widget__bar-chart"
          data-qa="LeadsWidgetChart"
        >
          <template #emptyStateArea>
            <VcLayout align-center justify-center class="leads-widget__empty-graph">
              <VcEmptyState
                :title="$t('widget.leads_widget.empty_graph.title')"
                :subtitle="$t('widget.leads_widget.empty_graph.subtitle')"
                :image="require('@/assets/images/widgets/no_clients.svg')"
                size="sm"
                data-qa="LeadsWidgetEmptyGraph"
              />
            </VcLayout>
          </template>
        </BarChart>
      </VcLayout>
    </VcLayout>
  </VcLayout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import BarChart from '@/modules/common/components/charts/BarChart/BarChart.vue';
import {
  buildLeadsWidgetChartData,
  hasLeadsChartData,
  SOURCE_COLORS,
  SOURCE_ROW_COLORS,
} from '../utils/leadsWidgetChart';

const CONTACT_SOURCE_NAMES = [
  'automatic_messages',
  'campaign',
  'client_portal',
  'initiated_by_staff',
  'staff_initiated',
  'website',
  'text_to_business_phone',
  'promotions_opt-in_by_keyword',
  'forward',
  'invitation',
  'google_reserve',
];

export default {
  name: 'LeadsWidget',
  components: {
    BarChart,
  },
  data() {
    return {
      hasLoadedChart: false,
      isPeriodMenuOpen: false,
    };
  },
  computed: {
    ...mapGetters('LeadsWidgetStore', [
      'leadsWidgetData',
      'chartBuckets',
      'selectedPeriod',
      'selectedChartPeriod',
      'isLoading',
      'showAiReceptionistMetrics',
    ]),
    periodOptions() {
      return [
        { value: 'year', label: this.$t('widget.leads_widget.periods.year') },
        { value: 'month', label: this.$t('widget.leads_widget.periods.month') },
        { value: 'week', label: this.$t('widget.leads_widget.periods.week') },
        { value: 'last_year', label: this.$t('widget.leads_widget.periods.last_year') },
        { value: 'last_month', label: this.$t('widget.leads_widget.periods.last_month') },
        { value: 'last_week', label: this.$t('widget.leads_widget.periods.last_week') },
      ];
    },
    selectedPeriodLabel() {
      return this.periodOptions.find((period) => period.value === this.selectedPeriod)?.label
        || this.periodOptions[0].label;
    },
    chartPeriodOptions() {
      return [
        { value: 'week', label: this.$t('widget.leads_widget.chart_periods.week') },
        { value: 'month', label: this.$t('widget.leads_widget.chart_periods.month') },
      ];
    },
    newLeads() {
      return this.leadsWidgetData?.totals?.new_leads || 0;
    },
    hasValidRange() {
      const range = this.leadsWidgetData?.range || {};
      return Boolean(range.start_date && range.end_date);
    },
    isNewLeadsLinkable() {
      return this.hasValidRange && this.newLeads > 0;
    },
    isAiReceptionistInstalled() {
      return this.leadsWidgetData?.ai_receptionist_metrics_source === 'api';
    },
    sourceRows() {
      return (this.leadsWidgetData?.sources || []).map((source, index) => ({
        ...source,
        label: this.sourceLabel(source),
        color: SOURCE_ROW_COLORS[index] || SOURCE_COLORS.unknown,
        linkable: this.hasValidRange && this.isSourceLinkable(source),
      }));
    },
    chartSourceRows() {
      const sourceRowsByKey = this.sourceRows.reduce((result, source) => ({
        ...result,
        [source.source_key]: source,
      }), {});
      const chartSourceKeys = [];

      (this.chartBuckets || []).forEach((bucket) => {
        Object.keys(bucket.sources || {}).forEach((sourceKey) => {
          if (!chartSourceKeys.includes(sourceKey)) {
            chartSourceKeys.push(sourceKey);
          }
        });
      });

      return chartSourceKeys.map((sourceKey, index) => {
        const source = sourceRowsByKey[sourceKey] || {
          source_key: sourceKey,
          color_key: sourceKey,
          fallback_label: sourceKey,
        };

        return {
          ...source,
          label: source.label || this.sourceLabel(source),
          color: source.color || SOURCE_ROW_COLORS[index] || SOURCE_COLORS.unknown,
        };
      });
    },
    chartData() {
      return buildLeadsWidgetChartData({
        buckets: this.chartBuckets,
        sources: this.chartSourceRows,
      });
    },
    chartOptions() {
      return {
        maintainAspectRatio: false,
        onClick: this.handleChartClick,
        onHover: this.handleChartHover,
        scales: {
          x: {
            stacked: true,
          },
          y: {
            stacked: true,
            beginAtZero: true,
            ticks: {
              stepSize: 1,
            },
          },
        },
      };
    },
    hasChartData() {
      return hasLeadsChartData({ buckets: this.chartBuckets });
    },
  },
  async created() {
    this.addI18nModule('widgets-pov');
    this.addI18nModule('client-pov');
    await this.fetchLeadsWidgetData({
      period: this.selectedPeriod,
      chartPeriod: this.selectedChartPeriod,
    });
    this.hasLoadedChart = true;
    this.$emit('track', 'View');
    this.$emit('ready');
  },
  methods: {
    ...mapActions('LeadsWidgetStore', ['fetchLeadsWidgetData']),
    togglePeriodMenu() {
      this.isPeriodMenuOpen = !this.isPeriodMenuOpen;
    },
    async selectPeriod(period) {
      this.isPeriodMenuOpen = false;
      await this.handlePeriodChange(period);
    },
    async handlePeriodChange(period) {
      if (period === this.selectedPeriod) return;
      await this.fetchLeadsWidgetData({
        period,
        chartPeriod: this.selectedChartPeriod,
      });
    },
    async handleChartPeriodChange(chartPeriod) {
      if (chartPeriod === this.selectedChartPeriod) return;
      await this.fetchLeadsWidgetData({
        period: this.selectedPeriod,
        chartPeriod,
      });
    },
    navigateToAiReceptionist() {
      this.$emit('sdkAction', 'Navigate', {
        type: 'relative',
        path: this.leadsWidgetData.ai_receptionist_path,
      });
    },
    handleAiReceptionistMetricClick() {
      if (this.isAiReceptionistInstalled) {
        this.navigateToAiReceptionist();
      }
    },
    isSourceLinkable(source) {
      const key = source.source_key;
      if (!key || key === 'other') {
        return false;
      }
      return Boolean(source.source_name);
    },
    navigateToCrm({ source, range } = {}) {
      const navigationRange = range || this.leadsWidgetData.range || {};
      const { start_date: createdFrom, end_date: createdTo } = navigationRange;
      if (!createdFrom || !createdTo) return;

      this.$emit('sdkAction', 'Navigate', {
        type: 'relative',
        path: '/app/clients',
        queryParams: {
          leads_filter: '1',
          created_from: createdFrom,
          created_to: createdTo,
          ...(source ? { source } : {}),
        },
      });
    },
    handleNewLeadsClick() {
      if (this.isNewLeadsLinkable) {
        this.navigateToCrm();
      }
    },
    handleSourceClick(source) {
      if (source.linkable) {
        this.navigateToCrm({ source: source.source_name });
      }
    },
    chartSourceFromElements(chart, elements = []) {
      const datasetIndex = elements[0]?.datasetIndex;
      if (datasetIndex === undefined) return null;

      const sourceKey = chart?.data?.datasets?.[datasetIndex]?.source_key;
      if (!sourceKey) return null;

      return this.chartSourceRows.find((source) => source.source_key === sourceKey);
    },
    chartBucketFromElements(elements = []) {
      const bucketIndex = elements[0]?.index;
      if (bucketIndex === undefined) return null;

      return this.chartBuckets?.[bucketIndex];
    },
    handleChartClick(_event, elements, chart) {
      const source = this.chartSourceFromElements(chart, elements);
      const bucket = this.chartBucketFromElements(elements);
      if (source?.linkable) {
        this.navigateToCrm({ source: source.source_name, range: bucket });
      }
    },
    handleChartHover(event, elements, chart) {
      const source = this.chartSourceFromElements(chart, elements);
      const bucket = this.chartBucketFromElements(elements);
      const canvas = event?.native?.target || chart?.canvas;
      if (canvas) {
        canvas.style.cursor = source?.linkable && bucket?.start_date && bucket?.end_date ? 'pointer' : 'default';
      }
    },
    sourceLabel(source) {
      const sourceName = source.source_name || source.source_key;
      const sourceKey = source.source_key;
      const labelKey = source.label_key;
      const sourceNameTranslationKey = `client.source.${sourceName}`;
      const sourceKeyTranslationKey = `client.source.${sourceKey}`;

      if (labelKey && this.$te?.(labelKey)) {
        return this.$t(labelKey);
      }

      if (sourceKey && this.$te?.(sourceKeyTranslationKey)) {
        return this.$t(sourceKeyTranslationKey);
      }

      if (sourceName && this.$te?.(sourceNameTranslationKey)) {
        return this.$t(sourceNameTranslationKey);
      }

      if (CONTACT_SOURCE_NAMES.includes(sourceName)) {
        return this.$t('client.source.unknown');
      }

      return source.fallback_label || sourceName || this.$t('client.source.unknown');
    },
    formatYAxisTick(value) {
      return Number.isInteger(value) && value >= 0 ? value : '';
    },
    formatTooltip(tooltipItem) {
      return `${tooltipItem.dataset.label}: ${tooltipItem.raw}`;
    },
    formatTooltipTitle(items) {
      return items?.[0]?.label || '';
    },
  },
};
</script>

<style scoped lang="scss">
$small: 598px;

.leads-widget {
  container-type: inline-size;
  color: var(--gray-darken-5);
  height: 100%;
  text-align: start;
  width: 100%;

  &__title {
    font-size: var(--font-size-medium2);
    font-weight: var(--font-weight-large2);
  }

  &__chart-periods {
    border: 1px solid var(--gray-lighten-2);
    border-radius: var(--border-radius-medium);
    display: flex;
    overflow: hidden;
  }

  &__chart-header {
    flex: 0 0 auto;
  }

  &__chart-period {
    background: transparent;
    border: 0;
    color: var(--gray-darken-3);
    cursor: pointer;
    font-size: var(--font-size-x-small);
    font-weight: var(--font-weight-medium2);
    padding: var(--size-value1) var(--size-value3);

    &--selected {
      background: var(--primary-lighten-5);
      color: var(--primary);
      font-weight: var(--font-weight-large);
    }
  }

  &__wrapper {
    gap: var(--size-value5);
    min-height: 0;
  }

  &__data {
    flex-grow: 0;
    flex-shrink: 0;
    width: 280px;
  }

  &__metric-label,
  &__section-title {
    font-size: var(--font-size-small2);
    font-weight: var(--font-weight-large2);
  }

  &__metric-label--inline {
    align-items: center;
    display: flex;
    gap: var(--size-value2);
  }

  &__period-selector {
    position: relative;
  }

  &__period-trigger,
  &__period-option {
    background: transparent;
    border: 0;
    cursor: pointer;
    font: inherit;
  }

  &__period-trigger {
    align-items: center;
    color: var(--link-color);
    display: inline-flex;
    gap: var(--size-value1);
    padding: 0;
  }

  &__period-caret {
    border-inline-end: 4px solid transparent;
    border-inline-start: 4px solid transparent;
    border-top: 5px solid currentColor;
    height: 0;
    width: 0;
  }

  &__period-menu {
    background: var(--white);
    border: 1px solid var(--gray-lighten-2);
    border-radius: var(--border-radius-medium);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    display: flex;
    flex-direction: column;
    inset-inline-start: 0;
    min-width: 112px;
    padding: var(--size-value1) 0;
    position: absolute;
    top: calc(100% + var(--size-value1));
    z-index: 1;
  }

  &__period-option {
    color: var(--gray-darken-5);
    font-size: var(--font-size-x-small);
    font-weight: var(--font-weight-medium2);
    padding: var(--size-value2) var(--size-value3);
    text-align: start;
    white-space: nowrap;

    &:hover,
    &--selected {
      background: var(--primary-lighten-5);
      color: var(--primary);
    }
  }

  &__metric-value {
    font-size: var(--font-size-h2);
    font-weight: var(--font-weight-large2);
    line-height: var(--size-value8);

    &--primary {
      font-size: var(--font-size-medium2);
    }

    &--clickable {
      cursor: pointer;
      width: fit-content;

      &:hover {
        color: var(--link-color);
      }
    }
  }

  &__metric-action {
    cursor: pointer;
    display: inline-flex;
    flex-direction: column;
    width: fit-content;

    &:hover {
      color: var(--link-color);
    }
  }

  &__setup-text {
    align-items: center;
    display: inline-flex;
    font-size: var(--font-size-x-small);
    gap: var(--size-value1);
    line-height: var(--size-value6);
  }

  &__setup-link {
    background: transparent;
    border: 0;
    color: var(--link-color);
    cursor: pointer;
    font: inherit;
    font-weight: var(--font-weight-large2);
    padding: 0;
  }

  &__sources {
    align-items: flex-start;
    display: flex;
    flex-direction: column;
  }

  &__source-row {
    align-items: center;
    display: inline-flex;
    font-size: var(--font-size-x-small);
    gap: var(--size-value1);
    line-height: var(--size-value6);
    width: fit-content;

    &--clickable {
      cursor: pointer;

      &:hover {
        color: var(--link-color);

        .leads-widget__source-label {
          text-decoration: underline;
        }
      }
    }
  }

  &__source-label {
    align-items: center;
    display: flex;
    min-width: 0;
  }

  &__source-dot {
    border-radius: 50%;
    display: inline-block;
    flex: 0 0 auto;
    height: var(--size-value2);
    margin-inline-end: var(--size-value2);
    width: var(--size-value2);
  }

  &__source-count {
    font-weight: var(--font-weight-large);
  }

  &__source-separator {
    color: var(--gray-darken-3);
  }

  &__chart {
    display: none;
    flex: 1;
    min-height: 250px;

    @container (min-width: #{$small}) {
      display: flex;
    }
  }

  &__bar-chart {
    flex: 1;
    min-height: 220px;
  }

  &__empty-graph {
    margin: 0 auto;
    max-width: 70%;
    text-align: center;
  }
}
</style>
